/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    Fade,
    Grid,
    IconButton,
    Modal,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";

import Backdrop from "@mui/material/Backdrop";
import { Material, MaterialDraft, ModalBase } from "../../utils/Interfaces";
import CloseIcon from "@mui/icons-material/Close";
import { computeAcquisitionPrice, computeProfit, computeRetailPrice, delayedAction } from "../../utils/Functions";
import { NumericFormat } from "react-number-format";
import { useCreateMaterialDatabaseEntry } from "./materialsHooks";

export interface CreateMaterialModalProps extends ModalBase {
    allMaterials: Material[];
}

function CreateMaterialModal(props: CreateMaterialModalProps) {
    const { open, handleClose, allMaterials } = props;

    const [name, setName] = useState<string>("");
    const [manufacturer, setManufacturer] = useState<string>("");
    const [partNumber, setPartNumber] = useState<string>("");

    const [price, setPrice] = useState<number>(0.0);
    const [salesTax, setSalesTax] = useState<number>(0.0);
    const [shippingPrice, setShippingPrice] = useState<number>(0.0);
    const [markup, setMarkup] = useState<number>(0.0);

    const [notes, setNotes] = useState<string>("");

    const { mutate, status } = useCreateMaterialDatabaseEntry(() => onClose());

    const theme = useTheme();

    function compileMaterialDraft(): MaterialDraft {
        return {
            name: name,
            part_number: partNumber,
            price: price,
            sales_tax: salesTax,
            shipping_price: shippingPrice,
            markup: markup / 100.0,
            notes: notes ?? "",
            manufacturer: manufacturer,
        };
    }

    function getFormErrors(): string {
        if (name === "" || name === null) {
            return "Material Name cannot be blank.";
        }

        if (partNumber === "" || partNumber === null) {
            return "Part # cannot be blank.";
        }

        return "";
    }

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setName("");
            setManufacturer("");
            setPartNumber("");
            setPrice(0.0);
            setSalesTax(0.0);
            setShippingPrice(0.0);
            setMarkup(0.0);
            setNotes("");
        });
    }

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={onClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="h5" textAlign="center" sx={{ ml: 2, mb: 2, width: "100%" }}>
                                Add New Material to Database
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start" sx={{ mr: -2 }}>
                                <IconButton aria-label="delete" onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {getFormErrors() !== "" && <Alert severity="error">{getFormErrors()}</Alert>}

                        {getFormErrors() === "" && <Alert severity="success">All entries are valid.</Alert>}

                        <Box component="form">
                            <Grid container xs={12}>
                                <Grid item xs={6} sx={{ pr: 1 }}>
                                    <TextField
                                        onChange={(event) => setName(event.target.value)}
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="name"
                                        label="Material Name"
                                        name="name"
                                        value={name}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ pl: 1 }}>
                                    <TextField
                                        onChange={(event) => setManufacturer(event.target.value)}
                                        margin="normal"
                                        fullWidth
                                        id="manufacturer"
                                        label="Manufacturer"
                                        name="manufacturer"
                                        value={manufacturer}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container xs={12}>
                                <Grid item xs={6} sx={{ pr: 1 }}>
                                    <TextField
                                        onChange={(event) => setPartNumber(event.target.value)}
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="part_number"
                                        label="Part #"
                                        name="part_number"
                                        value={partNumber}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container xs={12}>
                                <Grid item xs={12} sx={{ pr: 1 }}>
                                    <TextField
                                        onChange={(event) => setNotes(event.target.value)}
                                        multiline
                                        rows={4}
                                        margin="normal"
                                        fullWidth
                                        id="notes"
                                        label="Notes/Comments"
                                        name="notes"
                                        value={notes}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ mt: 2 }}>
                                <Typography>Material Pricing</Typography>
                            </Divider>

                            <Grid container xs={12} sx={{ mt: 2 }}>
                                <Grid item xs={6} sx={{ pr: 1, ml: -1 }}>
                                    <NumericFormat
                                        allowNegative={false}
                                        value={price}
                                        customInput={TextField}
                                        style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                        label="Unit Price"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        prefix="$ "
                                        onValueChange={(values) => {
                                            setPrice(values.floatValue ?? 0.0);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ pr: 1, ml: 1 }}>
                                    <NumericFormat
                                        allowNegative={false}
                                        value={salesTax}
                                        customInput={TextField}
                                        style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                        label="Sales Tax"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        prefix="$ "
                                        onValueChange={(values) => {
                                            setSalesTax(values.floatValue ?? 0.0);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ pr: 1, ml: -1, mt: 2 }}>
                                    <NumericFormat
                                        allowNegative={false}
                                        value={shippingPrice}
                                        customInput={TextField}
                                        style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                        label="Shipping Price"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        prefix="$ "
                                        onValueChange={(values) => {
                                            setShippingPrice(values.floatValue ?? 0.0);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ pr: 1, ml: 1, mt: 2 }}>
                                    <NumericFormat
                                        allowNegative={false}
                                        value={markup}
                                        customInput={TextField}
                                        style={{ marginLeft: 8, marginTop: 8, width: "100%" }}
                                        label="Markup %"
                                        thousandSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        suffix="%"
                                        onValueChange={(values) => {
                                            setMarkup(values.floatValue ?? 0.0);
                                        }}
                                    />
                                </Grid>
                                <Stack sx={{ pr: 1, ml: -1, mt: 4, width: "100%" }}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography color="info.main" sx={{ pl: 2 }}>
                                            Acquisition Price
                                        </Typography>
                                        <Typography color="info.main" sx={{ pl: 2 }}>
                                            ${computeAcquisitionPrice(price, salesTax, shippingPrice).toFixed(2)}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography color="info.main" sx={{ pl: 2 }}>
                                            Retail Price
                                        </Typography>
                                        <Typography color="info.main" sx={{ pl: 2 }}>
                                            ${computeRetailPrice(price, salesTax, shippingPrice, markup).toFixed(2)}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography color="success.main" sx={{ pl: 2 }}>
                                            Profit
                                        </Typography>
                                        <Typography color="success.main" sx={{ pl: 2 }}>
                                            ${computeProfit(price, shippingPrice, markup).toFixed(2)}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {status === "pending" && (
                                <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                                    <CircularProgress size={20} />
                                </Stack>
                            )}

                            {status !== "pending" && (
                                <Button
                                    disabled={getFormErrors() !== ""}
                                    onClick={() => mutate([compileMaterialDraft(), allMaterials])}
                                    fullWidth
                                    variant="contained"
                                    sx={{ mt: 3, mb: 2 }}
                                    color="primary"
                                >
                                    Submit
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
}

export default CreateMaterialModal;
